import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Loader2, Mail, Edit3, Trash2, Send, Sparkles, Eye, BarChart3 } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import { nlList, nlDelete, nlSubscriberStats, nlAIDraft } from '../lib/marketingApi';

const FILTERS = [{ k: '', label: 'All' }, { k: 'draft', label: 'Drafts' }, { k: 'sent', label: 'Sent' }];

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}

export default function NewsletterList() {
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [cats, setCats] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [draftCat, setDraftCat] = useState('');
  const catLabel = (slug) => (cats.find((c) => c.slug === slug)?.label || slug);

  useEffect(() => { nlSubscriberStats().then((d) => { setCats(d.categories || []); setDraftCat((d.categories || [])[0]?.slug || ''); }).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setItems(null);
    try { const d = await nlList(status ? { status } : {}); setItems(d.items || []); }
    catch (e) { toast.error('Failed to load newsletters'); setItems([]); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const aiDraft = async () => {
    if (!draftCat) return;
    setDrafting(true);
    try {
      const d = await nlAIDraft({ category: draftCat });
      toast.success('AI draft created from the latest market news');
      navigate(`/admin/newsletters/${d.newsletter.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'AI draft failed'); } finally { setDrafting(false); }
  };

  const remove = async () => {
    const it = confirmDelete;
    if (!it) return;
    setBusy(it.id);
    try { await nlDelete(it.id); toast.success('Deleted'); setConfirmDelete(null); load(); }
    catch (e) { toast.error('Delete failed'); } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="newsletter-list">
      <AdminHeader active="content" actions={canEdit && (
        <div className="flex items-center gap-2">
          <select value={draftCat} onChange={(e) => setDraftCat(e.target.value)} data-testid="nl-ai-category"
            className="h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-700 outline-none focus:border-[#0035A8]">
            {cats.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
          <button onClick={aiDraft} disabled={drafting} data-testid="nl-ai-draft-btn"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#0035A8] px-3 text-sm font-semibold text-[#0035A8] hover:bg-[#0035A8]/5 disabled:opacity-60">
            {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI draft
          </button>
          <button onClick={() => navigate('/admin/newsletters/new')} data-testid="nl-new-btn"
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white hover:bg-[#002B87]">
            <Plus className="h-4 w-4" /> New
          </button>
        </div>
      )} />
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><Mail className="h-4.5 w-4.5" /></span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Newsletters</h1>
            <p className="text-sm text-slate-500">AI-drafted weekly market updates — review, approve and send to subscribers</p>
          </div>
        </div>

        <div className="mb-4 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setStatus(f.k)} data-testid={`nl-filter-${f.label.toLowerCase()}`}
              className={`rounded-md px-3.5 py-1.5 text-sm font-semibold transition-all ${status === f.k ? 'bg-[#0035A8] text-white' : 'text-slate-500 hover:text-slate-800'}`}>{f.label}</button>
          ))}
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
            <div className="col-span-4">Subject</div><div className="col-span-2">Category</div>
            <div className="col-span-1">Status</div><div className="col-span-2 text-right">Sent / Opened</div>
            <div className="col-span-2">Updated</div><div className="col-span-1 text-right">Actions</div>
          </div>
          {items === null ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-slate-400"><Mail className="h-8 w-8" /><p className="text-sm">No newsletters yet. Use “AI draft” to generate one from live market news.</p></div>
          ) : items.map((it) => {
            const st = it.stats || {};
            return (
              <div key={it.id} data-testid={`nl-row-${it.id}`} className="grid grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3.5 md:grid-cols-12 md:items-center md:gap-3">
                <button onClick={() => navigate(`/admin/newsletters/${it.id}`)} className="col-span-4 min-w-0 text-left">
                  <div className="truncate text-sm font-semibold text-slate-900 hover:text-[#0035A8]">{it.subject}</div>
                  <div className="truncate text-xs text-slate-400">{it.auto_generated ? 'Auto-generated' : 'Manual'}{it.sections ? ` · ${it.sections.length} sections` : ''}</div>
                </button>
                <div className="col-span-2 text-sm text-slate-600">{catLabel(it.category)}</div>
                <div className="col-span-1"><span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${it.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{it.status === 'sent' ? 'Sent' : 'Draft'}</span></div>
                <div className="col-span-2 text-right text-sm font-semibold text-slate-700" data-testid={`nl-stats-${it.id}`}>{it.status === 'sent' ? <span className="inline-flex items-center gap-1"><BarChart3 className="h-3.5 w-3.5 text-slate-400" /> {(st.sent || 0)} / {(st.opened || 0)}</span> : <span className="text-slate-300">—</span>}</div>
                <div className="col-span-2 text-xs text-slate-500">{fmt(it.updated_at)}</div>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <button onClick={() => navigate(`/admin/newsletters/${it.id}`)} title={it.status === 'sent' ? 'View' : 'Edit'} data-testid={it.status === 'sent' ? `nl-view-${it.id}` : `nl-edit-${it.id}`} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-[#0035A8]">{it.status === 'sent' ? <Eye className="h-4 w-4" /> : <Edit3 className="h-4 w-4" />}</button>
                  {canEdit && it.status !== 'sent' && (
                    <button onClick={() => navigate(`/admin/newsletters/${it.id}`)} title="Send" data-testid={`nl-send-${it.id}`} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-emerald-600"><Send className="h-4 w-4" /></button>
                  )}
                  {canEdit && (<button onClick={() => setConfirmDelete(it)} disabled={busy === it.id} title="Delete" data-testid={`nl-delete-${it.id}`} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="nl-delete-confirm-modal" onClick={() => setConfirmDelete(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600"><Trash2 className="h-5 w-5" /></span>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Delete “{confirmDelete.subject}”?</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">This permanently deletes the newsletter and its stats. This cannot be undone. Are you sure you want to go ahead?</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} data-testid="nl-delete-confirm-cancel" className="inline-flex h-10 items-center rounded-md border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={remove} disabled={busy === confirmDelete.id} data-testid="nl-delete-confirm-yes" className="inline-flex h-10 items-center gap-1.5 rounded-md bg-rose-600 px-4 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60">{busy === confirmDelete.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Yes, delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
